import { Component, Vue, Prop } from 'vue-property-decorator';
import { Camera } from '@/models/camera';
import { Project } from '@/models/project';
import { User } from '@/models/user';
import ProjectModal from '@/components/projectmodal/projectmodal.component.vue';
import { City } from '@/models/city';
import { State, Getter } from 'vuex-class';
import { LMap, LTileLayer, LMarker, LPopup } from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.webpack.css'; // Re-uses images from ~leaflet package
import 'leaflet-defaulticon-compatibility';
import L, { LatLngBounds } from 'leaflet';

@Component({
  components: {
    Description,
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    ProjectModal,
  },
})
export default class Description extends Vue {
  @Prop({ type: Object, required: true }) private readonly camera!: Camera;
  @Prop({ type: Object, required: true }) private project!: Project;
  @Getter('projectWithId', { namespace: 'projects' }) readonly projectWithId!: (id: string) => Project | undefined;
  @State('locale', { namespace: 'profile' }) private readonly locale!: string;
  @State('projects', { namespace: 'projects' }) private allProject!: Project[];
  @State('user', { namespace: 'profile' }) private user!: User;

  private projects: Project[] = [];
  private visible = true;
  private url = 'http://mt0.google.com/vt/lyrs=y&hl=en&x={x}&y={y}&z={z}';
  private attribution = '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors';
  private center: [number, number] = [48.858093, 2.294694]; // Coordinates for Paris, France
  private zoom = 4;
  private showMap = false; // Set to true to display the map by default
  private showMosaicAndList = true;

  public get isAdmin(): boolean {
    if (this.user && this.project) {
      return (
        this.project.owner.id === this.user.id ||
        (this.project.administratorId && this.project.administratorId.includes(this.user.id))
      );
    }
    return false;
  }

  private mounted() {
    this.calculateMapBounds();
  }

  private convertToDecimal(coord: string, direction: 'N' | 'S' | 'E' | 'W'): number {
    const [degPart, minPartRaw] = coord.split('d ');
    const minutesMatch = minPartRaw.match(/([\d,.]+)m/);
    const degrees = parseFloat(degPart);
    const minutes = minutesMatch ? parseFloat(minutesMatch[1].replace(',', '.')) : 0;
    const decimal = degrees + minutes / 60;
    return direction === 'S' || direction === 'W' ? -decimal : decimal;
  }

  // Retourne la position du marqueur (shadow si disponible, sinon projet)
  private readonly CAM_POS_OVERRIDES: Record<string, Record<string, [number, number]>> = {
    // Exemple pour CE projet (remplace par ton projectId réel)
    '903aca66-756d-4ed3-afb3-4bb1a195abe9': {
      IrisV2048: [48.229469, 4.102976],
      IrisV2075: [48.229103, 4.098145],
    },
  };

  /** Optionnel : lire une valeur persistée (localStorage) si définie */
  private getLocalOverride(projectId: string, cameraId: string): [number, number] | null {
    const k = `campos:${projectId}:${cameraId}`;
    const v = window.localStorage.getItem(k);
    if (!v) return null;
    try {
      const { lat, lng } = JSON.parse(v);
      if (typeof lat === 'number' && typeof lng === 'number') return [lat, lng];
    } catch {
      // ignore parsing error
    }
    return null;
  }

  private getMarkerPosition(): [number, number] {
    const projectId = this.project?.id;
    const camId = (this.camera as any).id ?? (this.camera as any).Id;

    // 0) Override local (localStorage prioritaire pour pouvoir ajuster sans recompiler)
    const loc = projectId ? this.getLocalOverride(projectId, camId) : null;
    if (loc) return loc;

    // 1) Override codé en dur pour ce projet uniquement
    const projMap = projectId ? this.CAM_POS_OVERRIDES[projectId] : undefined;
    const hard = projMap ? projMap[camId] : undefined;
    if (hard) return hard;

    // 2) Shadow si présent
    if (this.camera.shadow?.reported?.Position_Latitude && this.camera.shadow?.reported?.Position_Longitude) {
      const latString = this.camera.shadow.reported.Position_Latitude;
      const lngString = this.camera.shadow.reported.Position_Longitude;
      const lat = this.convertToDecimal(latString, latString.includes('S') ? 'S' : 'N');
      const lng = this.convertToDecimal(lngString, lngString.includes('W') ? 'W' : 'E');
      return [lat, lng];
    }

    // 3) Fallback: ville du projet
    return [this.project.city.latlng.lat, this.project.city.latlng.lng];
  }

  private async calculateMapBounds() {
    const project = this.project;
    if (!project) return;

    const position = this.getMarkerPosition();
    const [lat, lng] = position;

    const paddingFactor = 0.2;
    const bounds = new L.LatLngBounds([lat, lng], [lat, lng]).pad(paddingFactor);

    this.center = [lat, lng];
    const map = this.$refs.map as any;
    map.fitBounds(bounds);
  }
}
